import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RecordUuid, RecordViewModel } from 'services/device/domain/model/RecordModel';

import { AsyncFetchStatus } from 'store/common/AsyncFetchStatus';
import { AsyncReducerStatus } from 'store/common/AsyncReducerStatus';
import { RootState } from 'store/store';

import { DebugConsole } from 'lib/debug/DebugConsole';
import { PropellerError } from 'lib/persistence/http/error/PropellerError';
import { ModelQueryOrderClosureBuilder } from 'lib/persistence/idb/query/ModelQueryOrderClosureBuilder';

import {
	CreatableDocumentViewModel as CreatableViewModel,
	DocumentModelConverter as ModelConverter,
	DocumentStoreModel as StoreModel,
	DocumentViewModel as ViewModel,
} from 'services/documents/domain/model/DocumentModel';
import { DocumentModelScope } from 'services/documents/domain/model/DocumentModelScope';
import { DocumentModelState } from 'services/documents/domain/model/DocumentModelState';
import { DocumentPersistence as Persistence } from 'services/documents/persistence/DocumentPersistence';
import { checkFetchStatus, checkFetchStatusPending } from '../../../store/common/AsyncFetchStatus.util';
import { DocumentHttpPersistence as HttpPersistence } from '../persistence/DocumentHttpPersistence';

// Declare a document state type
export interface DocumentState {
	documents: Array<StoreModel>;
	createdDocuments: Array<StoreModel> | null;
	fetchStatus: AsyncFetchStatus;
	lastFetchError: Error | PropellerError | null;
	actionStatus: AsyncReducerStatus;
	lastActionError: Error | PropellerError | null;
}

// The initial state
const initialState = {
	documents: [] as Array<StoreModel>,
	createdDocuments: null,
	fetchStatus: AsyncFetchStatus.INITIAL,
	lastFetchError: null,
	actionStatus: AsyncReducerStatus.IDLE,
	lastActionError: null
} as DocumentState;

// Implementation of the async actions
// It is required to declare them before declaring the slice because the block constant has to be defined before using it as the

export const fetchDocuments = createAsyncThunk(
	'documents/fetch',
	async (params: { clientUuid: string; facilityUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollection();
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return checkFetchStatus(documents.fetchStatus);
		}
	}
);

export const fetchLatestDocuments = createAsyncThunk(
	'documents/latest',
	async (params: { clientUuid: string; facilityUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchLatestCollection();
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return checkFetchStatus(documents.fetchStatus);
		}
	}
);

export const fetchDocumentsByMaintenanceLogEntry = createAsyncThunk(
	'documents/fetchByMaintenanceLogEntry',
	async (params: { clientUuid: string; facilityUuid: string; maintenanceLogEntryUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollectionByMaintenanceLogEntry(params.maintenanceLogEntryUuid);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Sliently abort the action
			const { documents } = getState() as RootState;
			return !checkFetchStatusPending(documents.fetchStatus);
		}
	}
);

export const fetchDocumentsByDevice = createAsyncThunk(
	'documents/fetchByDevice',
	async (params: { clientUuid: string; facilityUuid: string; deviceUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollectionByDevice(params.deviceUuid);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return !checkFetchStatusPending(documents.fetchStatus);
		}
	}
);

export const fetchDocumentsByRecord = createAsyncThunk(
	'documents/fetchByRecord',
	async (params: { clientUuid: string; facilityUuid: string; recordUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollectionByRecord(params.recordUuid);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return !checkFetchStatusPending(documents.fetchStatus);
		}
	}
);

export const fetchDocumentsByFolder = createAsyncThunk(
	'documents/fetchByFolder',
	async (params: { clientUuid: string; facilityUuid: string; documentfFolderUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollectionByFolder(params.documentfFolderUuid);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return !checkFetchStatusPending(documents.fetchStatus);
		}
	}
);

export const fetchDocumentsByFacility = createAsyncThunk(
	'documents/fetchByFacility',
	async (params: { clientUuid: string; facilityUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new HttpPersistence(params.clientUuid, params.facilityUuid);
		return persistence.fetchCollectionByFacility();
	},
	{
		condition: (_params, { getState }): boolean => {
			// Silently abort the action
			const { documents } = getState() as RootState;
			return !checkFetchStatusPending(documents.fetchStatus);
		}
	}
);

export const resetDocuments = createAsyncThunk(
	'documents/reset',
	async (params: { clientUuid: string; facilityUuid: string }): Promise<Array<StoreModel>> => {
		const persistence = new Persistence(params.clientUuid, params.facilityUuid);
		return persistence.resetCollection();
	},
	{
		condition: (_params, { getState }): boolean => {
			// Sliently abort the action
			const { documents } = getState() as RootState;
			return checkFetchStatus(documents.fetchStatus);
		}
	}
);

export const updateDocument = createAsyncThunk(
	'document/update',
	async (viewModel: ViewModel): Promise<StoreModel> => {
		const persistence = new Persistence(viewModel.Client, viewModel.Facility);
		return persistence.updateEntity(viewModel);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Abort the action with an error
			const { documents } = getState() as RootState;
			if (documents.actionStatus !== AsyncReducerStatus.IDLE) {
				throw new Error('Action not available');
			}
			return true;
		}
	}
);

export const createDocuments = createAsyncThunk(
	'document/create',
	async (params: { clientUuid: string; facilityUuid: string, viewModels: Array<CreatableViewModel> }): Promise<Array<StoreModel>> => {
		const persistence = new Persistence(params.clientUuid, params.facilityUuid);
		return persistence.createEntities(params.viewModels);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Abort the action with an error
			const { documents } = getState() as RootState;
			if (documents.actionStatus !== AsyncReducerStatus.IDLE) {
				throw new Error('Action not available');
			}
			return true;
		}
	}
);

export const deleteDocument = createAsyncThunk(
	'document/delete',
	async (viewModel: ViewModel): Promise<StoreModel> => {
		const persistence = new Persistence(viewModel.Client, viewModel.Facility);
		return persistence.deleteEntity(viewModel);
	},
	{
		condition: (_params, { getState }): boolean => {
			// Abort the action with an error
			const { documents } = getState() as RootState;
			if (documents.actionStatus !== AsyncReducerStatus.IDLE) {
				throw new Error('Action not available');
			}
			return true;
		}
	}
);

// Slice definition
export const documentSlice = createSlice({
	name: 'documents',
	initialState,
	// Regular synchronous reducers
	reducers: {
		resetState(state) {
			Object.assign(state, initialState);
		},
		resetActionStatus(state) {
			state.actionStatus = AsyncReducerStatus.IDLE;
		}
	},
	// Extra reducers required to handle async actions; the returning promise is resolved to the according reducer
	// Attention: Because we use Redux Toolkit´s creates slice utility we can also mtutate the state directly. It is internally handled by
	// Immer. See https://redux.js.org/recipes/structuring-reducers/immutable-update-patterns and https://github.com/immerjs/immer.
	extraReducers: {
		[String(fetchDocuments.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocuments.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocuments.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchLatestDocuments.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchLatestDocuments.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchLatestDocuments.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchDocumentsByMaintenanceLogEntry.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocumentsByMaintenanceLogEntry.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocumentsByMaintenanceLogEntry.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchDocumentsByDevice.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocumentsByDevice.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocumentsByDevice.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchDocumentsByRecord.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocumentsByRecord.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocumentsByRecord.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchDocumentsByFolder.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocumentsByFolder.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocumentsByFolder.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(fetchDocumentsByFacility.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(fetchDocumentsByFacility.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(fetchDocumentsByFacility.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		 [String(resetDocuments.pending)]: (state) => {
			if (state.fetchStatus === AsyncFetchStatus.INITIAL) {
				state.fetchStatus = AsyncFetchStatus.INITIAL_PENDIG;
			} else {
				state.fetchStatus = AsyncFetchStatus.PENDING;
			}
		},
		[String(resetDocuments.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents = action.payload;
			state.fetchStatus = AsyncFetchStatus.SUCCESS;
		},
		[String(resetDocuments.rejected)]: (state, action) => {
			state.fetchStatus = AsyncFetchStatus.FAILED;
			state.lastFetchError = action.error;
		},
		[String(updateDocument.pending)]: (state) => {
			state.actionStatus = AsyncReducerStatus.UPDATE_PENDING;
		},
		[String(updateDocument.fulfilled)]: (state, action: PayloadAction<StoreModel>) => {
			const index = state.documents.findIndex((entry): boolean => {
				return entry.Uuid === action.payload.Uuid;
			}) ?? null;
			if (index !== null) {
				state.documents[index] = action.payload;
			}
			state.documents = state.documents.sort(new ModelQueryOrderClosureBuilder<StoreModel>().build('Filename'));
			state.actionStatus = AsyncReducerStatus.UPDATED;
		},
		[String(updateDocument.rejected)]: (state, action) => {
			state.lastActionError = action.error;
			state.actionStatus = AsyncReducerStatus.FAILED;
		},
		[String(createDocuments.pending)]: (state) => {
			state.actionStatus = AsyncReducerStatus.CREATE_PENDING;
			state.createdDocuments = null;
		},
		[String(createDocuments.fulfilled)]: (state, action: PayloadAction<Array<StoreModel>>) => {
			state.documents.push(...action.payload);
			state.documents = state.documents.sort(new ModelQueryOrderClosureBuilder<StoreModel>().build('Filename'));
			state.createdDocuments = action.payload;
			state.actionStatus = AsyncReducerStatus.CREATED;
		},
		[String(createDocuments.rejected)]: (state, action) => {
			state.lastActionError = action.error;
			state.actionStatus = AsyncReducerStatus.FAILED;
		},
		[String(deleteDocument.pending)]: (state) => {
			state.actionStatus = AsyncReducerStatus.DELETE_PENDING;
		},
		[String(deleteDocument.fulfilled)]: (state, action: PayloadAction<StoreModel>) => {
			const index = state.documents.findIndex((entry): boolean => {
				return entry.Uuid === action.payload.Uuid;
			}) ?? null;
			if (index !== null) {
				state.documents.splice(index, 1);
			}
			state.actionStatus = AsyncReducerStatus.DELETED;
		},
		[String(deleteDocument.rejected)]: (state, action) => {
			state.actionStatus = AsyncReducerStatus.FAILED;
			state.lastActionError = action.error.message;
		}
	}
});

export const { resetState, resetActionStatus } = documentSlice.actions;

// Export the reducer as default
export default documentSlice.reducer;

// Selector functions to be used with useSelector or useTypedSelector to read from the state
export const selectDocumentsByDate = (clientUuid: string, facilityUuid: string, limit: number): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		let storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid;
		}) ?? [];
		storeModels = storeModels.sort((left, right) => {
			if (left.CreatedAt === right.CreatedAt) {
				return 0;
			}
			return left.CreatedAt < right.CreatedAt ? 1 : -1;
		});
		let viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		viewModels = viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
		return viewModels.slice(0, limit);
	};
};

export const selectFacilityDocuments = (
	clientUuid: string,
	facilityUuid: string
): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.DOCUMENT_FOLDER as string;
		}) ?? [];

		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectDocumentsByFolder = (clientUuid: string, facilityUuid: string, folderUuid: string): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.DOCUMENT_FOLDER
				&& storeModel.ScopeReference === folderUuid;
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectFilteredDocumentsByFolder = (
	clientUuid: string,
	facilityUuid: string,
	folderUuid: string,
	includeActive?: boolean,
	includeArchived?: boolean
): (rootState: RootState) => ReadonlyArray<ViewModel> => {

	if (includeActive && includeArchived || !includeActive && !includeArchived) {
		return selectDocumentsByFolder(clientUuid, facilityUuid, folderUuid);
	}

	const filterModelState = includeActive ? DocumentModelState.ACTIVE : DocumentModelState.ARCHIVED;

	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.DOCUMENT_FOLDER
				&& storeModel.State === String(filterModelState)
				&& storeModel.ScopeReference === folderUuid;
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectDocumentsByDevice = (clientUuid: string, facilityUuid: string, deviceUuid: string): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.DEVICE
				&& storeModel.ScopeReference === deviceUuid;
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectDocumentsByRecord = (clientUuid: string, facilityUuid: string, recordUuid: string): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.RECORD
				&& storeModel.ScopeReference === recordUuid;
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export type DocumentsByRecordMap = Map<RecordUuid, Array<ViewModel>>;

export const selectDocumentsByRecordsMap = (records: ReadonlyArray<RecordViewModel>): (rootState: RootState) => DocumentsByRecordMap => {
	return (rootState: RootState): DocumentsByRecordMap => {
		const recordUuids = records.map((value): RecordUuid => {
			return value.Uuid;
		});
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Scope === DocumentModelScope.RECORD
				&& recordUuids.includes(storeModel.ScopeReference);
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		}).filter((viewModel) => {
			return viewModel !== null;
		});

		const documentsByRecordMap: DocumentsByRecordMap = new Map();
		for (const viewModel of viewModels) {
			if (!documentsByRecordMap.has(viewModel.ScopeReference)) {
				documentsByRecordMap.set(viewModel.ScopeReference, []);
			}
			documentsByRecordMap.get(viewModel.ScopeReference).push(viewModel);
		}
		return documentsByRecordMap;
	};
};

export const selectDocumentsByMaintenanceLogEntry = (
	clientUuid: string,
	facilityUuid: string,
	maintenanceLogEntryUuid: string
): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.MAINTENANCE_LOG_ENTRY
				&& storeModel.ScopeReference === maintenanceLogEntryUuid;
		}) ?? [];
		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectFilteredDocumentsByDevice = (
	clientUuid: string,
	facilityUuid: string,
	deviceUuid: string,
	includeActive: boolean,
	includeArchived: boolean
): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	if (includeActive && includeArchived || !includeActive && !includeArchived) {
		return selectDocumentsByDevice(clientUuid, facilityUuid, deviceUuid);
	}

	const filterModelState = includeActive ? DocumentModelState.ACTIVE : DocumentModelState.ARCHIVED;

	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.State === String(filterModelState)
				&& storeModel.Scope === DocumentModelScope.DEVICE as string
				&& storeModel.ScopeReference === deviceUuid;
		}) ?? [];

		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectDeviceDocuments = (
	clientUuid: string,
	facilityUuid: string
): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.Scope === DocumentModelScope.DEVICE as string;
		}) ?? [];

		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectFilteredDeviceDocuments = (
	clientUuid: string,
	facilityUuid: string,
	includeActive: boolean,
	includeArchived: boolean
): (rootState: RootState) => ReadonlyArray<ViewModel> => {
	if (includeActive && includeArchived || !includeActive && !includeArchived) {
		return selectDeviceDocuments(clientUuid, facilityUuid);
	}

	const filterModelState = includeActive ? DocumentModelState.ACTIVE : DocumentModelState.ARCHIVED;

	return (rootState: RootState): ReadonlyArray<ViewModel> => {
		const storeModels = rootState.documents.documents.filter((storeModel): boolean => {
			return storeModel.Client === clientUuid
				&& storeModel.Facility === facilityUuid
				&& storeModel.State === String(filterModelState)
				&& storeModel.Scope === DocumentModelScope.DEVICE as string;
		}) ?? [];

		const viewModels = storeModels.map((storeModel): ViewModel | null => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel) => {
			return viewModel !== null;
		});
	};
};

export const selectDocumentByUuid = (uuid: string): (rootState: RootState) => ViewModel | null => {
	return (rootState: RootState): ViewModel | null => {
		const storeModel = rootState.documents.documents.find((sModel): boolean => {
			return sModel.Uuid === uuid;
		}) ?? null;
		try {
			return new ModelConverter().fromStoreModel(storeModel).toViewModel();
		} catch (error) {
			DebugConsole.error(error);
			return null;
		}
	};
};

export const selectCreatedDocuments = (): (rootState: RootState) => Array<ViewModel> | null => {
	return (rootState: RootState): Array<ViewModel> | null => {
		const storeModels = rootState.documents.createdDocuments;
		if (storeModels === null) {
			return null;
		}
		const viewModels = storeModels.map((storeModel): ViewModel => {
			try {
				return new ModelConverter().fromStoreModel(storeModel).toViewModel();
			} catch (error) {
				DebugConsole.error(error);
				return null;
			}
		});
		return viewModels.filter((viewModel): boolean => {
			return viewModel !== null;
		});
	};
};
